import { z } from 'zod'

// =====================
// Portal expense coding (client-portal financials Phase 3, D3)
// =====================
// ExpenseCodingItem is an entity-scoped intake object, NOT an Expense: portal
// users add transactions (CSV upload or manual entry) and code them against
// per-entity dropdowns; staff disposition coded rows, and accepting one
// creates (or matches) an Expense through the normal staff workflow.

/** VarChar lifecycle on expense_coding_items.status — no Prisma enum. */
export const EXPENSE_CODING_STATUS = {
  UNCODED: 'UNCODED',
  CODED: 'CODED',
  ACCEPTED: 'ACCEPTED',
  DISMISSED: 'DISMISSED',
} as const
export type ExpenseCodingStatus =
  (typeof EXPENSE_CODING_STATUS)[keyof typeof EXPENSE_CODING_STATUS]

/** Statuses a portal user still owns (editable, deletable, codable). */
export const EXPENSE_CODING_OPEN_STATUSES = [
  EXPENSE_CODING_STATUS.UNCODED,
  EXPENSE_CODING_STATUS.CODED,
] as const

export const EXPENSE_CODING_SOURCE = {
  CSV: 'CSV',
  MANUAL: 'MANUAL',
} as const
export type ExpenseCodingSource =
  (typeof EXPENSE_CODING_SOURCE)[keyof typeof EXPENSE_CODING_SOURCE]

export const EXPENSE_CODING_MAX_DROPDOWNS = 10
export const EXPENSE_CODING_MAX_OPTIONS = 100
export const EXPENSE_CODING_IMPORT_ROW_CAP = 2000

// =====================
// Per-entity dropdown config
// =====================

export const ExpenseCodingDropdownSchema = z.object({
  // Stable id the coded values key on; slug-ish, staff never edit it directly.
  id: z
    .string()
    .trim()
    .min(1)
    .max(50)
    .regex(/^[a-z0-9-]+$/, 'Dropdown ids are lowercase slugs'),
  label: z.string().trim().min(1).max(100),
  options: z
    .array(z.string().trim().min(1).max(200))
    .min(1)
    .max(EXPENSE_CODING_MAX_OPTIONS),
})
export type ExpenseCodingDropdown = z.infer<typeof ExpenseCodingDropdownSchema>

export const UpdateExpenseCodingConfigSchema = z
  .object({
    dropdowns: z
      .array(ExpenseCodingDropdownSchema)
      .max(EXPENSE_CODING_MAX_DROPDOWNS)
      .superRefine((dropdowns, ctx) => {
        const ids = new Set<string>()
        for (const dropdown of dropdowns) {
          if (ids.has(dropdown.id)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Duplicate dropdown id: ${dropdown.id}`,
            })
          }
          ids.add(dropdown.id)
        }
      }),
    collectBillable: z.boolean(),
    // Whether the client can add transactions themselves (manual entry and
    // CSV upload). Staff upload is always available regardless.
    allowClientUpload: z.boolean(),
  })
  .strict()
export type UpdateExpenseCodingConfigDTO = z.infer<
  typeof UpdateExpenseCodingConfigSchema
>

export interface ExpenseCodingConfigDTO {
  entityId: string
  dropdowns: ExpenseCodingDropdown[]
  collectBillable: boolean
  allowClientUpload: boolean
}

// =====================
// Portal intake (manual entry + CSV commit share this row shape)
// =====================

/** Calendar dates travel as YYYY-MM-DD strings; the API stores UTC midnight. */
export const ExpenseCodingDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the YYYY-MM-DD date format')

export const PortalCreateCodingItemSchema = z
  .object({
    transactionDate: ExpenseCodingDateSchema,
    description: z.string().trim().min(1).max(500),
    amount: z.number().positive().max(999999999.99),
    currency: z.string().trim().min(3).max(10).optional(),
  })
  .strict()
export type PortalCreateCodingItemDTO = z.infer<
  typeof PortalCreateCodingItemSchema
>

export const PortalCommitCodingImportSchema = z
  .object({
    rows: z
      .array(PortalCreateCodingItemSchema)
      .min(1)
      .max(EXPENSE_CODING_IMPORT_ROW_CAP),
  })
  .strict()
export type PortalCommitCodingImportDTO = z.infer<
  typeof PortalCommitCodingImportSchema
>

// =====================
// Portal coding
// =====================

export const PortalCodeItemSchema = z
  .object({
    // Dropdown id -> chosen option; validated against the entity config
    // server-side (unknown ids / options rejected).
    coding: z.record(z.string().max(50), z.string().max(200)).optional(),
    billable: z.boolean().optional(),
    clientNote: z.string().trim().max(1000).nullable().optional(),
    // Revert to UNCODED, clearing coding fields. Mutually exclusive with the
    // fields above (service-enforced).
    uncode: z.boolean().optional(),
  })
  .strict()
export type PortalCodeItemDTO = z.infer<typeof PortalCodeItemSchema>

// =====================
// Staff disposition
// =====================

export const AcceptCodingItemSchema = z
  .object({
    // When present, link this existing expense instead of creating one.
    existingExpenseId: z.string().uuid().optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .strict()
export type AcceptCodingItemDTO = z.infer<typeof AcceptCodingItemSchema>

export const DismissCodingItemSchema = z
  .object({
    note: z.string().trim().max(1000).optional(),
  })
  .strict()
export type DismissCodingItemDTO = z.infer<typeof DismissCodingItemSchema>

// =====================
// Response DTOs (whitelists — portal DTOs never carry disposition fields)
// =====================

export interface PortalExpenseCodingItemDTO {
  id: string
  source: ExpenseCodingSource
  status: ExpenseCodingStatus
  transactionDate: string
  description: string
  amount: number
  currency: string
  coding: Record<string, string> | null
  billable: boolean
  clientNote: string | null
  codedAt: string | null
  createdAt: string
}

export interface PortalExpenseCodingListDTO {
  open: PortalExpenseCodingItemDTO[]
  /** Recently dispositioned rows so clients see closure (status only). */
  recent: PortalExpenseCodingItemDTO[]
}

export interface StaffExpenseCodingItemDTO extends PortalExpenseCodingItemDTO {
  organizationId: string
  entityId: string
  entityName: string | null
  codedBy: string | null
  dispositionedBy: string | null
  dispositionedAt: string | null
  dispositionNote: string | null
  createdExpenseId: string | null
}

/** One parsed CSV row in the preview response. */
export interface ExpenseCodingImportPreviewRow {
  rowNumber: number
  transactionDate: string | null
  description: string | null
  amount: number | null
  currency: string | null
  /** Blocking problems; a row with errors is excluded from commit. */
  errors: string[]
  /**
   * The queue already holds a row with this date, amount, and description
   * (any status). Advisory in the preview; commit skips duplicates again
   * server-side, so a stale preview cannot double-insert.
   */
  duplicate: boolean
}

export interface ExpenseCodingImportPreviewDTO {
  rows: ExpenseCodingImportPreviewRow[]
  validCount: number
  errorCount: number
  duplicateCount: number
  /** File-level blocking diagnostics (missing columns, over the row cap). */
  fileErrors: string[]
}

export interface ExpenseCodingImportCommitResultDTO {
  created: number
  /** Rows skipped at commit because an identical queue row already existed. */
  skippedDuplicates: number
}
